import axios from "axios";
import { t } from "i18next";
import { z } from "zod";

import languageCodeList from "../../constants/languages";

import type { AxiosError } from "axios";

import type {
  FastSchedulingConflictSlot,
  MediaTranslation,
} from "../../api/MediaAPI.types";
import type { LanguageCode } from "../../api/ProjectAPI.types";
import type { Picture, Pictures, TranslationsIndexes } from "../../types";

export function getMediaTranslationSchema() {
  return z.object({
    language: z.enum(languageCodeList),
    language_uuid: z.string().optional(),
    default: z.boolean(),
    uuid: z.string().optional(),
    name: z
      .string()
      .min(1, t("form:required", { field: t("form:fields.title") })),
    slug: z.string().optional(),
    description: z
      .string()
      .nullable()
      .optional()
      .transform((v) => v ?? ""),
    short_description: z
      .string()
      .nullable()
      .optional()
      .transform((v) => v ?? ""),
    picture: z.custom<Picture>().optional(),
  });
}

export function getMediaTranslationsFormSchema() {
  return z.object({ translations: z.array(getMediaTranslationSchema()) });
}

export interface MediaTranslationFormField {
  language: LanguageCode;
  language_uuid?: string;
  default: boolean;
  uuid?: string;
  name: string;
  slug?: string;
  description?: string | null;
  short_description?: string | null;
  picture?: Picture;
}

export interface MediaCreateTranslationInput {
  uuid: string;
  language_uuid: string;
}

export interface MediaTranslationToCreate {
  uuid?: string;
  default: boolean;
  language: string;
  language_uuid: string;
  name: string;
  slug: string;
  description: string;
  short_description: string;
  picture?: Picture;
  pictures: Pictures | null;
  temp_pictures: null;
}

export function apiToFormTranslations(
  translations: MediaTranslation[],
): MediaTranslationFormField[] {
  return [...translations]
    .sort((a, b) => {
      if (a.default) return -1;
      if (b.default) return 1;
      return a.language.localeCompare(b.language);
    })
    .map((translation) => ({
      language: translation.language as LanguageCode,
      language_uuid: translation.language_uuid,
      default: translation.default,
      uuid: translation.uuid,
      name: translation.name ?? "",
      slug: translation.slug ?? "",
      description: translation.description ?? "",
      short_description: translation.short_description ?? "",
      picture: translation.picture ?? undefined,
    }));
}

export function createTranslationResponseToFormField(
  response: MediaCreateTranslationInput,
  language: LanguageCode,
): MediaTranslationFormField {
  return {
    uuid: response.uuid,
    default: false,
    language_uuid: response.language_uuid,
    language,
    name: "",
    slug: "",
    description: "",
    short_description: "",
  };
}

interface MediaApiError {
  message: string;
  reason: string;
}

export function getMediaApiErrorMessageList(error: unknown): string[] {
  if (!axios.isAxiosError(error)) return [];

  const apiErrors = (error as AxiosError<{ error: MediaApiError[] | string }>)
    .response?.data?.error;

  if (!Array.isArray(apiErrors) || !apiErrors.length) return [];

  return apiErrors.map(({ message, reason }) => {
    const translationKey = reason
      ? `error:${reason}.${message}`
      : `error:${message}`;
    return t(translationKey);
  });
}

interface FastSchedulingConflictBody {
  error: string;
  slots?: FastSchedulingConflictSlot[];
}

export function getFastSchedulingConflictSlotList(
  error: unknown,
): FastSchedulingConflictSlot[] | null {
  if (!axios.isAxiosError(error)) return null;

  const body = (error as AxiosError<FastSchedulingConflictBody>).response?.data;

  return body?.error === "fast_scheduling_conflict" ? (body.slots ?? []) : null;
}

export function formToApiTranslations(
  formTranslations: MediaTranslationFormField[],
): {
  translations: MediaTranslationToCreate[];
  translations_indexes: TranslationsIndexes;
} {
  const translations: MediaTranslationToCreate[] = formTranslations.map(
    (field) => ({
      uuid: field.uuid,
      default: field.default,
      language: field.language,
      language_uuid: field.language_uuid ?? "",
      name: field.name,
      slug: field.slug ?? "",
      description: field.description ?? "",
      short_description: field.short_description ?? "",
      picture: field.picture,
      pictures: null,
      temp_pictures: null,
    }),
  );

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const language_uuid: Record<string, number[]> = {};
  let defaultIndex = 0;

  formTranslations.forEach((field, index) => {
    if (field.language_uuid) {
      language_uuid[field.language_uuid] = [index];
    }
    if (field.default) {
      defaultIndex = index;
    }
  });

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const translations_indexes: TranslationsIndexes = {
    language_uuid,
    default: { true: [defaultIndex] },
  };

  return { translations, translations_indexes };
}
